// ═══════════════════════════════════════════════════════════════
// vaultZkCrypto.js — Coffre-fort V2 zero-knowledge (Session G)
// ═══════════════════════════════════════════════════════════════
//
// Crypto pure WebCrypto, zéro dépendance npm. Tout le matériel
// secret reste côté navigateur. Le serveur ne reçoit que des blobs
// chiffrés (wrapped_dek, test_blob, recovery_blob, items envelopes).
//
// Format envelope : `${base64(iv12)}.${base64(ct + tag16)}` — AES-GCM
// concatène automatiquement le tag d'authentification au ciphertext.
//
// Algos :
//   PBKDF2-SHA512 600 000 itérations → KEK 256 bits
//   AES-GCM 256 / IV 96 bits / tag 128 bits
//   BIP39 24 mots = 256 bits entropie + 8 bits checksum SHA-256
// ═══════════════════════════════════════════════════════════════

import { BIP39_WORDLIST_EN } from './bip39WordlistEn';

const enc = new TextEncoder();
const dec = new TextDecoder();

export const VAULT_ZK_DEFAULT_ITER = 600000;
export const VAULT_ZK_KDF_LABEL = 'pbkdf2-sha512-v1';
const TEST_BLOB_PLAINTEXT = 'itriia-vault-zk-v1';

// ─────────────────────────────────────────────────────────────
// Helpers binaires
// ─────────────────────────────────────────────────────────────

function bytesToBase64(bytes) {
  const arr = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  let bin = '';
  for (let i = 0; i < arr.length; i++) bin += String.fromCharCode(arr[i]);
  return btoa(bin);
}

function base64ToBytes(b64) {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes;
}

export function randomBytes(n) {
  const arr = new Uint8Array(n);
  window.crypto.getRandomValues(arr);
  return arr;
}

export function randomSaltBase64(byteLen = 32) {
  return bytesToBase64(randomBytes(byteLen));
}

function packEnvelope(iv, ctBuffer) {
  return `${bytesToBase64(iv)}.${bytesToBase64(new Uint8Array(ctBuffer))}`;
}

function unpackEnvelope(envelopeStr) {
  const dot = envelopeStr.indexOf('.');
  if (dot < 0) throw new Error('vault-zk: invalid envelope format');
  return {
    iv: base64ToBytes(envelopeStr.slice(0, dot)),
    ct: base64ToBytes(envelopeStr.slice(dot + 1)),
  };
}

// ─────────────────────────────────────────────────────────────
// KEK — dérivation PBKDF2 depuis passphrase
// ─────────────────────────────────────────────────────────────

export async function deriveKEKFromPassphrase(passphrase, saltBase64, iter = VAULT_ZK_DEFAULT_ITER) {
  if (!passphrase || passphrase.length < 8) {
    throw new Error('vault-zk: passphrase trop courte (min 8 caractères techniques)');
  }
  const salt = base64ToBytes(saltBase64);
  const baseKey = await window.crypto.subtle.importKey(
    'raw',
    enc.encode(passphrase.normalize('NFKC')),
    'PBKDF2',
    false,
    ['deriveKey']
  );
  return window.crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt, iterations: iter, hash: 'SHA-512' },
    baseKey,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
}

// ─────────────────────────────────────────────────────────────
// DEK — clé maître items, random AES-256
// ─────────────────────────────────────────────────────────────

export async function generateDEK() {
  return window.crypto.subtle.generateKey(
    { name: 'AES-GCM', length: 256 },
    true,
    ['encrypt', 'decrypt']
  );
}

export async function wrapDEK(dek, kek) {
  const dekRaw = await window.crypto.subtle.exportKey('raw', dek);
  const iv = randomBytes(12);
  const ct = await window.crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    kek,
    dekRaw
  );
  return packEnvelope(iv, ct);
}

export async function unwrapDEK(wrappedB64, kek) {
  try {
    const { iv, ct } = unpackEnvelope(wrappedB64);
    const dekRaw = await window.crypto.subtle.decrypt(
      { name: 'AES-GCM', iv },
      kek,
      ct
    );
    return await window.crypto.subtle.importKey(
      'raw',
      dekRaw,
      { name: 'AES-GCM', length: 256 },
      true,
      ['encrypt', 'decrypt']
    );
  } catch (e) {
    return null;
  }
}

// ─────────────────────────────────────────────────────────────
// Test blob — challenge pour valider passphrase sans déwrap DEK
// ─────────────────────────────────────────────────────────────

export async function createTestBlob(kek) {
  const iv = randomBytes(12);
  const ct = await window.crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    kek,
    enc.encode(TEST_BLOB_PLAINTEXT)
  );
  return packEnvelope(iv, ct);
}

export async function verifyTestBlob(testBlobB64, kek) {
  try {
    const { iv, ct } = unpackEnvelope(testBlobB64);
    const pt = await window.crypto.subtle.decrypt(
      { name: 'AES-GCM', iv },
      kek,
      ct
    );
    return dec.decode(pt) === TEST_BLOB_PLAINTEXT;
  } catch (e) {
    return false;
  }
}

// ─────────────────────────────────────────────────────────────
// Items — encrypt / decrypt avec DEK
// ─────────────────────────────────────────────────────────────

export async function encryptItem(plaintext, dek) {
  const iv = randomBytes(12);
  const ct = await window.crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    dek,
    enc.encode(plaintext)
  );
  return packEnvelope(iv, ct);
}

export async function decryptItem(envelopeB64, dek) {
  try {
    const { iv, ct } = unpackEnvelope(envelopeB64);
    const pt = await window.crypto.subtle.decrypt(
      { name: 'AES-GCM', iv },
      dek,
      ct
    );
    return dec.decode(pt);
  } catch (e) {
    return null;
  }
}

// ─────────────────────────────────────────────────────────────
// BIP39 — mnémonique 24 mots (256 bits entropie + 8 bits checksum)
// ─────────────────────────────────────────────────────────────

function byteToBits(byte) {
  const bits = new Array(8);
  for (let i = 0; i < 8; i++) bits[i] = (byte >> (7 - i)) & 1;
  return bits;
}

export async function generateMnemonic24() {
  const entropy = randomBytes(32);
  const hashBuf = await window.crypto.subtle.digest('SHA-256', entropy);
  const checksumByte = new Uint8Array(hashBuf)[0];

  const bits = [];
  for (let i = 0; i < entropy.length; i++) bits.push(...byteToBits(entropy[i]));
  bits.push(...byteToBits(checksumByte));

  const words = [];
  for (let i = 0; i < 24; i++) {
    let idx = 0;
    for (let j = 0; j < 11; j++) idx = (idx << 1) | bits[i * 11 + j];
    words.push(BIP39_WORDLIST_EN[idx]);
  }
  return words;
}

export async function validateMnemonic24(words) {
  if (!Array.isArray(words) || words.length !== 24) return false;
  const indices = words.map(w => BIP39_WORDLIST_EN.indexOf(String(w).toLowerCase().trim()));
  if (indices.some(i => i < 0)) return false;

  const bits = [];
  for (let i = 0; i < indices.length; i++) {
    for (let j = 10; j >= 0; j--) bits.push((indices[i] >> j) & 1);
  }
  const entropy = new Uint8Array(32);
  for (let i = 0; i < 32; i++) {
    let b = 0;
    for (let j = 0; j < 8; j++) b = (b << 1) | bits[i * 8 + j];
    entropy[i] = b;
  }
  let checksum = 0;
  for (let j = 0; j < 8; j++) checksum = (checksum << 1) | bits[256 + j];

  const hashBuf = await window.crypto.subtle.digest('SHA-256', entropy);
  return checksum === new Uint8Array(hashBuf)[0];
}

export async function deriveKEKFromMnemonic(words, recoverySaltBase64, iter = VAULT_ZK_DEFAULT_ITER) {
  const phrase = words.map(w => String(w).toLowerCase().trim()).join(' ');
  const salt = base64ToBytes(recoverySaltBase64);
  const baseKey = await window.crypto.subtle.importKey(
    'raw',
    enc.encode(phrase),
    'PBKDF2',
    false,
    ['deriveKey']
  );
  return window.crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt, iterations: iter, hash: 'SHA-512' },
    baseKey,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
}

// ─────────────────────────────────────────────────────────────
// Activation V2 — package complet à envoyer au backend /activate
// ─────────────────────────────────────────────────────────────

export async function buildActivationPayload(passphrase) {
  const salt = randomSaltBase64(32);
  const recoverySalt = randomSaltBase64(32);
  const iter = VAULT_ZK_DEFAULT_ITER;

  const kek = await deriveKEKFromPassphrase(passphrase, salt, iter);
  const dek = await generateDEK();
  const wrappedDek = await wrapDEK(dek, kek);
  const testBlob = await createTestBlob(kek);

  const mnemonic = await generateMnemonic24();
  const recoveryKEK = await deriveKEKFromMnemonic(mnemonic, recoverySalt, iter);
  const recoveryBlob = await wrapDEK(dek, recoveryKEK);

  return {
    payload: {
      salt,
      iter,
      kdf: VAULT_ZK_KDF_LABEL,
      wrapped_dek: wrappedDek,
      test_blob: testBlob,
      recovery_blob: recoveryBlob,
      recovery_salt: recoverySalt,
    },
    mnemonic,
    dek,
  };
}

// ─────────────────────────────────────────────────────────────
// Rotation passphrase — re-wrap DEK avec nouvelle KEK
// ─────────────────────────────────────────────────────────────

export async function buildRotationPayload(currentPassphrase, currentChallenge, newPassphrase) {
  const currentKek = await deriveKEKFromPassphrase(
    currentPassphrase,
    currentChallenge.salt,
    currentChallenge.iter
  );
  const ok = await verifyTestBlob(currentChallenge.test_blob, currentKek);
  if (!ok) return { error: 'wrong_passphrase' };

  const dek = await unwrapDEK(currentChallenge.wrapped_dek, currentKek);
  if (!dek) return { error: 'wrong_passphrase' };

  const newSalt = randomSaltBase64(32);
  const newKek = await deriveKEKFromPassphrase(newPassphrase, newSalt, VAULT_ZK_DEFAULT_ITER);
  const newWrapped = await wrapDEK(dek, newKek);
  const newTest = await createTestBlob(newKek);

  return {
    payload: {
      salt: newSalt,
      iter: VAULT_ZK_DEFAULT_ITER,
      kdf: VAULT_ZK_KDF_LABEL,
      wrapped_dek: newWrapped,
      test_blob: newTest,
    },
  };
}

// ─────────────────────────────────────────────────────────────
// Recovery — re-wrap DEK avec nouvelle passphrase via mnémonique
// ─────────────────────────────────────────────────────────────

export async function buildRecoveryPayload(mnemonic, currentChallenge, newPassphrase) {
  const ok = await validateMnemonic24(mnemonic);
  if (!ok) return { error: 'invalid_mnemonic' };

  const recoveryKek = await deriveKEKFromMnemonic(
    mnemonic,
    currentChallenge.recovery_salt,
    currentChallenge.iter
  );
  const dek = await unwrapDEK(currentChallenge.recovery_blob, recoveryKek);
  if (!dek) return { error: 'mnemonic_does_not_match' };

  const newSalt = randomSaltBase64(32);
  const newKek = await deriveKEKFromPassphrase(newPassphrase, newSalt, VAULT_ZK_DEFAULT_ITER);
  const newWrapped = await wrapDEK(dek, newKek);
  const newTest = await createTestBlob(newKek);

  return {
    payload: {
      salt: newSalt,
      iter: VAULT_ZK_DEFAULT_ITER,
      kdf: VAULT_ZK_KDF_LABEL,
      wrapped_dek: newWrapped,
      test_blob: newTest,
    },
    dek,
  };
}

// ─────────────────────────────────────────────────────────────
// Force passphrase — heuristique sobre, retourne 0..4
// ─────────────────────────────────────────────────────────────

export function passphraseStrengthScore(p) {
  if (!p) return 0;
  let s = 0;
  if (p.length >= 12) s++;
  if (p.length >= 16) s++;
  if (/[a-z]/.test(p) && /[A-Z]/.test(p)) s++;
  if (/\d/.test(p)) s++;
  if (/[^a-zA-Z0-9]/.test(p)) s++;
  return Math.min(s, 4);
}

export const _internal = { bytesToBase64, base64ToBytes, packEnvelope, unpackEnvelope };
